import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { ROLE_BADGE } from "@/lib/format";
import { Plus, Trash2, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = [{ v: "admin", l: "Admin" }, { v: "ops", l: "Ops (Sales & Shopper)" }, { v: "finance", l: "Finance" }];
const empty = { email: "", name: "", role: "ops", password: "" };

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [requests, setRequests] = useState([]);
  const [resetReq, setResetReq] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/password-requests")]);
    setUsers(u.data); setRequests(r.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const purgeDemo = async () => {
    if (!window.confirm("Hapus data demo lama (event JP-2026-TOKYO / TH-2026-BKK, katalog, customer & akun demo)? Data asli tidak tersentuh.")) return;
    try {
      const { data } = await api.post("/admin/purge-demo-data");
      const n = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      toast.success(n ? `Data demo dibersihkan (${n} record)` : "Tidak ada data demo yang tersisa");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolveRequest = async (dismiss = false) => {
    if (!dismiss && resetPw.length < 6) return toast.error("Password baru minimal 6 karakter");
    try {
      await api.post(`/password-requests/${resetReq.id}/resolve`, { new_password: dismiss ? null : resetPw });
      toast.success(dismiss ? "Permintaan diabaikan" : `Password ${resetReq.email} berhasil di-reset — sampaikan ke anggota`);
      setResetReq(null); setResetPw(""); load();
      window.dispatchEvent(new Event("pw-requests-changed"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ email: u.email, name: u.name, role: u.role, password: "" }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.email || !form.name) return toast.error("Email dan nama wajib diisi");
    try {
      if (editId) await api.put(`/users/${editId}`, { name: form.name, role: form.role, password: form.password || null });
      else await api.post("/users", { ...form, password: form.password || null });
      toast.success(editId ? "Anggota diperbarui" : "Anggota ditambahkan — bisa langsung masuk via Google");
      if (editId && form.password) window.dispatchEvent(new Event("pw-requests-changed"));
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Hapus ${u.email} dari tim? Akun ini tidak bisa masuk lagi.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Anggota dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const columns = [
    { key: "name", label: "Nama", render: (r) => <span className="font-semibold text-slate-900">{r.name}{r.id === user.id && <span className="ml-1 text-[10px] text-slate-400">(Anda)</span>}</span> },
    { key: "email", label: "Email", mono: true },
    { key: "role", label: "Role", render: (r) => <Badge label={r.role === "ops" ? "Ops (Sales & Shopper)" : r.role} cls={ROLE_BADGE[r.role] || ""} /> },
    { key: "login_method", label: "Login", render: (r) => r.login_method === "google" ? <Badge label="Google saja" cls="bg-blue-50 text-blue-700 border-blue-300" /> : <Badge label="Password + Google" cls="bg-slate-50 text-slate-600 border-slate-300" />, raw: (r) => r.login_method || "password" },
    { key: "last_login", label: "Login Terakhir", render: (r) => r.last_login ? new Date(r.last_login).toLocaleString("id-ID") : <span className="text-slate-300">—</span>, raw: (r) => r.last_login || "" },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => (
      <div className="flex items-center gap-1">
        <button data-testid={`edit-user-${r.email}`} className="p-1.5 text-slate-400 hover:text-slate-700" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></button>
        {r.id !== user.id && <button data-testid={`delete-user-${r.email}`} className="p-1.5 text-rose-400 hover:text-rose-600" onClick={() => remove(r)}><Trash2 className="w-4 h-4" /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Tim & Akses" subtitle="Tambahkan email anggota tim dan role-nya. Anggota bisa langsung masuk dengan Google tanpa perlu password.">
        {<Button data-testid="btn-add-user" onClick={openNew} className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /> Tambah Anggota</Button>}
      </PageHeader>
      {requests.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="password-requests">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-2"><KeyRound className="w-4 h-4" /> {requests.length} permintaan reset password menunggu</div>
          <ul className="divide-y divide-amber-200">
            {requests.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`pw-request-${r.email}`}>
                <span className="font-semibold text-slate-900">{r.user_name}</span>
                <span className="font-mono text-slate-600">{r.email}</span>
                <span className="text-xs text-slate-500">· {new Date(r.requested_at).toLocaleString("id-ID")}</span>
                {r.note && <span className="text-xs text-slate-600 italic">“{r.note}”</span>}
                <Button size="sm" className="ml-auto h-7 text-xs bg-slate-900 hover:bg-slate-800" data-testid={`resolve-request-${r.email}`} onClick={() => { setResetReq(r); setResetPw(""); }}>Atur Password Baru</Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Dialog open={!!resetReq} onOpenChange={(v) => !v && setResetReq(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password — {resetReq?.user_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Akun <span className="font-mono">{resetReq?.email}</span>. Sampaikan password baru ke anggota secara langsung; minta mereka menggantinya setelah login.</p>
            <div><Label>Password Baru</Label><PasswordInput data-testid="reset-new-password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Minimal 6 karakter" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="reset-dismiss" onClick={() => resolveRequest(true)}>Abaikan</Button>
            <Button data-testid="reset-save" onClick={() => resolveRequest(false)} className="bg-slate-900 hover:bg-slate-800">Simpan Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DataTable testId="team-table" columns={columns} data={users} searchKeys={["name", "email", "role"]} exportName="tim" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit Anggota" : "Tambah Anggota Tim"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Email (akun Google)</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editId} placeholder="nama@gmail.com" /></div>
            <div><Label>Nama</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Password <span className="text-slate-400 font-normal">(opsional — kosongkan jika hanya via Google)</span></Label><PasswordInput data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "Isi untuk mengganti password" : ""} /></div>
          </div>
          <DialogFooter><Button data-testid="save-user" onClick={save} className="bg-slate-900 hover:bg-slate-800">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="mt-8 rounded-lg border border-dashed border-slate-300 p-3 flex flex-wrap items-center gap-3" data-testid="danger-zone">
        <div className="text-sm"><div className="font-semibold text-slate-800">Bersihkan Data Demo</div><div className="text-xs text-slate-500">Hapus event contoh (JP-2026-TOKYO, TH-2026-BKK) beserta katalog, order, customer, dan akun demo dari build lama. Data asli aman.</div></div>
        <Button variant="outline" size="sm" className="ml-auto border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="btn-purge-demo" onClick={purgeDemo}><Trash2 className="w-4 h-4 mr-1" /> Bersihkan Data Demo</Button>
      </div>
    </div>
  );
}
